// ============================================================================
// ACCESS AREAS (frontend mirror) — 2026-08-05
// ----------------------------------------------------------------------------
// A deliberate, test-pinned DUPLICATE of backend/src/config/access-areas.js.
//
// Why duplicated rather than shared: the backend must never import from src/
// (it breaks the Docker image — the backend Dockerfile copies only backend/,
// so `../../../src/...` resolves to nothing at runtime; see the backend route
// discipline in CLAUDE.md). And the frontend must be able to decide what to
// render on the very first paint, before any request has returned, or the nav
// would visibly reshuffle a beat after load on every single boot.
//
// The duplicate is kept honest by src/__tests__/access-areas-agree.test.js,
// which deep-compares AREAS, ROLE_RANK and ROLE_AREA_DEFAULTS across the two
// modules and fails CI on any drift. This is the same guard-the-duplicate
// pattern the repo already uses for module flags
// (module-flag-defaults-agree.test.js).
//
// AUTHORITY: the server is the enforcement layer. What this module computes is
// only what to SHOW. Every gated route re-checks on the backend, so a user who
// forges a value here gains a menu entry and a 403, not access.
// ============================================================================

export interface AccessArea {
    id: string;
    label: string;
    group: 'work' | 'commercial' | 'money' | 'ai' | 'admin' | 'account';
    description: string;
    alwaysAllowed?: boolean;
    /** Reachable by the QuoteMaker platform operator only, and never grantable
     *  to a tenant by a per-user override. See resolveAreaAccess. */
    platformOnly?: boolean;
}

export const AREAS: readonly AccessArea[] = Object.freeze([
    { id: 'dashboard', label: 'Run Sheet', group: 'work', alwaysAllowed: true,
      description: 'The dispatch surface: every project, lanes, search and actions' },
    { id: 'settings', label: 'Settings', group: 'account', alwaysAllowed: true,
      description: 'Their own account, password and preferences' },

    { id: 'quotemaker', label: 'QuoteMaker', group: 'work',
      description: 'Create AI-assisted quotes through the Stage 1-5 pipeline' },
    { id: 'quick-edit', label: 'Quick Edit', group: 'work',
      description: 'Reopen and amend a quote that has already been produced' },
    { id: 'project-imports', label: 'Import Projects', group: 'work',
      description: 'Queue RFQ and site-documentation folders for drafting' },
    { id: 'intake', label: 'Intake', group: 'work',
      description: 'Triage incoming RFQs and dispatch site visits' },
    { id: 'site-visits', label: 'Site Visits', group: 'work',
      description: 'Capture photos, videos and notes on a site visit, and confirm it' },

    { id: 'analysis', label: 'Analysis', group: 'commercial',
      description: 'Business intelligence: volume, trade mix, ageing, outcomes' },
    { id: 'clients', label: 'Clients', group: 'commercial',
      description: 'Companies, contacts and client relationships' },
    { id: 'receivables', label: 'Receivables', group: 'commercial',
      description: 'Unpaid invoice context before recovery contact' },

    { id: 'business-training', label: 'Pricing', group: 'money',
      description: 'Business rates, supplier prices and the pricing method' },
    { id: 'quote-price-list', label: 'Quote Prices', group: 'money',
      description: 'The charge-out price list behind every quote dollar' },

    // 'training' (AI learnings, paired training, gold standards) was removed
    // with the learning loop (old pipeline removal, wave 4, 2026-11-02). A
    // stored override for it is skipped, as on the server.
    { id: 'aimodels', label: 'AI Models', group: 'ai',
      description: 'Which AI model handles each task' },

    { id: 'users', label: 'People', group: 'admin',
      description: 'Invite people, set their access, reset their passwords' },
    { id: 'audit', label: 'Activity Logs', group: 'admin',
      description: 'The audit trail of who did what' },
    { id: 'developer', label: 'Developer', group: 'admin',
      description: 'Pipelines, prompts and live monitoring' },
    { id: 'component-library', label: 'Component Library', group: 'admin',
      description: 'Design-system reference' },

    // The operator's own screen. Not a tenant capability: it is about OTHER
    // companies, so it is platformOnly and no override can grant it.
    { id: 'estate', label: 'Control Panel', group: 'admin', platformOnly: true,
      description: 'Every business using QuoteMaker, and the switches on each' },
]);

export const AREA_IDS: readonly string[] = Object.freeze(AREAS.map((a) => a.id));
export const ALWAYS_ALLOWED_AREA_IDS: readonly string[] = Object.freeze(
    AREAS.filter((a) => a.alwaysAllowed).map((a) => a.id),
);

export const ROLE_RANK: Readonly<Record<string, number>> = Object.freeze({
    platform_admin: 100,
    superadmin: 100,
    owner: 80,
    admin: 60,
    manager: 50,
    member: 40,
    assessor: 40,
    intake_admin: 35,
    site_doc_tech: 20,
    viewer: 10,
});

const ALL_BUSINESS = [
    'quotemaker', 'quick-edit', 'project-imports', 'intake',
    // Admins and owners get the assessor's surface too, otherwise nobody who
    // can support it can actually look at it. It is a phone screen, so it is
    // not where they work — but a rail row they can reach beats telling them
    // to forge a role to reproduce a report.
    'site-visits',
    'analysis', 'clients', 'receivables',
    'business-training', 'quote-price-list',
    'aimodels',
];

export const ROLE_AREA_DEFAULTS: Readonly<Record<string, readonly string[]>> = Object.freeze({
    // 'estate' is on these two ONLY. An owner or admin runs one business and
    // has no business reading a roll call of the others, however senior they
    // are inside their own tenant.
    platform_admin: Object.freeze([...ALL_BUSINESS, 'users', 'audit', 'developer', 'component-library', 'estate']),
    superadmin: Object.freeze([...ALL_BUSINESS, 'users', 'audit', 'developer', 'component-library', 'estate']),
    owner: Object.freeze([...ALL_BUSINESS, 'users', 'audit', 'developer', 'component-library']),
    admin: Object.freeze([...ALL_BUSINESS, 'users', 'audit', 'developer', 'component-library']),
    manager: Object.freeze([
        'quotemaker', 'quick-edit', 'project-imports', 'intake',
        'analysis', 'clients', 'receivables',
        'business-training', 'quote-price-list',
    ]),
    member: Object.freeze([
        'quotemaker', 'quick-edit', 'project-imports', 'analysis', 'clients',
    ]),
    // Site Assessor: a site-visit capture role, on a phone. NOT a quoting role —
    // the whole point of the surface is that they get capture-and-confirm
    // instead of the Stage 1-5 cockpit (Peter, 2026-08-16). They keep 'intake'
    // OUT deliberately: reaching their own visits must not also mean triaging
    // the queue, reassigning people or driving the state machine.
    //
    // TWO IDS, ONE JOB. 'assessor' (migration 001) and 'site_doc_tech'
    // (migration 051, "Site Documentation Technician") are the SAME person —
    // the one who attends the property and captures it. Peter, 2026-08-17:
    // "Assessor is the same as Site Documentation Technician. They are the ones
    // that visit the site." They therefore resolve to the SAME areas and carry
    // the SAME label; only 'assessor' is offered for new assignments (see
    // ASSIGNABLE_ROLES) so an operator is never asked to choose between two
    // names for one job. Use isSiteVisitRole() rather than comparing to either
    // string, so the second spelling can never be forgotten at a call site.
    //
    // site_doc_tech previously resolved to ['intake'] — the triage queue. That
    // was backwards: it gave the person who attends the property the office
    // admin's screen and withheld their own. Corrected 2026-08-17. Zero rows
    // held either role at the time (live check: admin x2, platform_admin x1).
    assessor: Object.freeze(['site-visits']),
    site_doc_tech: Object.freeze(['site-visits']),
    intake_admin: Object.freeze(['intake', 'clients']),
    viewer: Object.freeze(['analysis']),
});

/**
 * The two role ids that mean "this person attends the property and captures
 * it" — a Site Assessor, a.k.a. a Site Documentation Technician.
 *
 * One job, two spellings, for historical reasons only: 'assessor' came from
 * migration 001, 'site_doc_tech' from migration 051 when the intake side-app
 * needed a name for the same person. Never compare a role to either literal;
 * ask this instead, so adding a third spelling (or retiring one) is a one-line
 * change rather than a hunt through the tree.
 */
export const SITE_VISIT_ROLES: readonly string[] = Object.freeze(['assessor', 'site_doc_tech']);

/** True when this role attends properties and captures them. */
export function isSiteVisitRole(role: string | null | undefined): boolean {
    return SITE_VISIT_ROLES.includes(String(role || ''));
}

export function roleRank(role: string | null | undefined): number {
    return ROLE_RANK[String(role || '')] ?? 0;
}

/**
 * Display names for EVERY role, not just the assignable ones.
 *
 * Frontend-only, deliberately: the backend renders no UI, and this is not part
 * of the mirrored contract the agreement test pins.
 *
 * It has to cover the full set rather than reusing the assignable-role list,
 * because the two are not the same. `platform_admin` is deliberately absent
 * from ASSIGNABLE_ROLES (nobody picks it from a menu; it is granted out of band
 * by scripts/grant-super-admin.mjs), and the legacy values manager/assessor/
 * site_doc_tech/superadmin are held by real rows but are not offered for new
 * assignments. A lookup that falls back to the raw id shows a builder the
 * string `platform_admin`, which is a database value, not a label.
 */
export const ROLE_LABELS: Readonly<Record<string, string>> = Object.freeze({
    platform_admin: 'Super Admin',
    superadmin: 'Super Admin',
    owner: 'Owner',
    admin: 'Administrator',
    manager: 'Manager',
    member: 'Member',
    // One job, one name. 'site_doc_tech' used to render as "Site Tech", which
    // read as a different role to "Assessor" and was the source of the
    // confusion Peter flagged on 2026-08-17. Both ids now show the same words,
    // because they describe the same person.
    assessor: 'Site Assessor',
    site_doc_tech: 'Site Assessor',
    intake_admin: 'Office Admin',
    viewer: 'Viewer',
});

/** Human label for a role id. Never returns a raw slug. */
export function roleLabel(role: string | null | undefined): string {
    const id = String(role || '');
    if (!id) return 'No role';
    if (ROLE_LABELS[id]) return ROLE_LABELS[id];
    // An unknown value still gets a readable form rather than being hidden:
    // silently showing nothing about someone's role is worse than showing it.
    return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Mirror of the backend resolver. See the module header for the authority note. */
export function resolveAreaAccess(
    role: string | null | undefined,
    overrides?: Record<string, unknown> | null,
): Record<string, boolean> {
    const granted = new Set<string>(ROLE_AREA_DEFAULTS[String(role || '')] || []);
    const map: Record<string, boolean> = {};
    for (const area of AREAS) {
        let allowed = area.alwaysAllowed === true || granted.has(area.id);
        if (overrides && typeof overrides === 'object' && typeof overrides[area.id] === 'boolean') {
            allowed = overrides[area.id] as boolean;
        }
        if (area.alwaysAllowed === true) allowed = true;
        // The CEILING is the mirror of the floor above, and exists for the
        // opposite reason. A platform-only area is not a tenant capability at
        // all: it is the QuoteMaker operator's own screen, about other
        // companies. So the per-user override is inert for it, and the ONLY
        // way to hold it is to be the platform role. Without this, the People
        // screen could hand a customer's admin a rail row that answers 403
        // forever, because the route re-checks the role and not the area.
        // `&& allowed` and not a bare assignment: the ceiling must NARROW,
        // never widen, and never flatten. An override can still REVOKE the
        // console from an operator account (that is a real need), it just
        // cannot grant it to anybody the role default did not already reach.
        if (area.platformOnly === true) allowed = granted.has(area.id) && allowed;
        map[area.id] = allowed;
    }
    return map;
}

export function canAccessArea(
    role: string | null | undefined,
    overrides: Record<string, unknown> | null | undefined,
    areaId: string,
): boolean {
    if (!AREA_IDS.includes(areaId)) return false;
    return resolveAreaAccess(role, overrides)[areaId] === true;
}

export function canManageUsers(
    role: string | null | undefined,
    overrides?: Record<string, unknown> | null,
): boolean {
    return canAccessArea(role, overrides, 'users');
}

/**
 * The access map the UI should gate on, given whatever the app currently knows
 * about the signed-in person.
 *
 * `user.access` is the server's answer (from GET /api/auth/me) and always wins.
 * Falling back to the local role computation matters for the first paint after
 * a reload — App boots its `user` from the JWT claims synchronously, and /me
 * lands a moment later. Without the fallback the nav would render empty and
 * then fill in, which reads as a broken app.
 */
export function effectiveAccess(user: {
    role?: string | null;
    access?: Record<string, boolean> | null;
    areaOverrides?: Record<string, unknown> | null;
} | null | undefined): Record<string, boolean> {
    if (user?.access && typeof user.access === 'object') return user.access;
    return resolveAreaAccess(user?.role, user?.areaOverrides);
}

/**
 * Pages that already have a synchronous render guard in App. Keep this list
 * narrow: an effect-only redirect is not an access boundary because the page
 * could mount once before the effect runs.
 */
export const ROUTE_GUARDED_AREA_IDS: readonly string[] = Object.freeze([
    'developer',
    'intake',
    'site-visits',
]);

/** Return the guarded area id when one of those pages is explicitly denied. */
export function deniedAppAreaForPage(
    pageId: string | null | undefined,
    access: Record<string, boolean> | null | undefined,
): string | null {
    const id = String(pageId || '');
    if (!ROUTE_GUARDED_AREA_IDS.includes(id)) return null;
    return access?.[id] === true ? null : id;
}

/**
 * The only role-specific landing in QuoteMaker is the site assessor. Admins
 * may support Site Visits, but that permission does not make it their home.
 */
export function accessDeniedFallbackPage(
    user: { role?: string | null } | null | undefined,
    access: Record<string, boolean> | null | undefined,
): 'site-visits' | 'dashboard' {
    return isSiteVisitRole(user?.role) && access?.['site-visits'] === true
        ? 'site-visits'
        : 'dashboard';
}

export interface DeniedAppAreaRoute {
    deniedArea: string;
    fallbackPage: 'site-visits' | 'dashboard';
}

/**
 * Resolve one guarded navigation against the current access object. App runs
 * this again whenever `/auth/me` supplies or changes the active access map.
 */
export function resolveDeniedAppAreaRoute(
    pageId: string | null | undefined,
    user: {
        role?: string | null;
        access?: Record<string, boolean> | null;
        areaOverrides?: Record<string, unknown> | null;
    } | null | undefined,
): DeniedAppAreaRoute | null {
    const access = effectiveAccess(user);
    const deniedArea = deniedAppAreaForPage(pageId, access);
    if (!deniedArea) return null;
    return {
        deniedArea,
        fallbackPage: accessDeniedFallbackPage(user, access),
    };
}
